// Local data store using localStorage with JSON file defaults.
// On first load, copies JSON defaults into localStorage.
// Admin panel writes here. Customer pages read from here.
// When we go live, swap this for Supabase calls.

import defaultContent from '../data/siteContent.json';
import defaultProducts from '../data/products.json';
import defaultShipping from '../data/shippingZones.json';
import defaultEmails from '../data/emailTemplates.json';
import defaultPromos from '../data/promoCodes.json';
import defaultPages from '../data/collectionPages.json';

const KEYS = {
  content: 'aml_site_content',
  products: 'aml_products',
  shipping: 'aml_shipping_zones',
  emails: 'aml_email_templates',
  promos: 'aml_promo_codes',
  orders: 'aml_orders',
  messages: 'aml_contact_messages',
  subscribers: 'aml_newsletter_subscribers',
  pages: 'aml_collection_pages',
};

function getOrInit(key, defaults) {
  const stored = localStorage.getItem(key);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      // Corrupted data, reset
    }
  }
  localStorage.setItem(key, JSON.stringify(defaults));
  return defaults;
}

function save(key, data) {
  localStorage.setItem(key, JSON.stringify(data));
}

// ---- Site Content ----
export function getSiteContent() {
  return getOrInit(KEYS.content, defaultContent);
}

export function updateSiteContent(key, value) {
  const content = getSiteContent();
  content[key] = value;
  save(KEYS.content, content);
  return content;
}

export function updateSiteContentBulk(updates) {
  const content = getSiteContent();
  Object.assign(content, updates);
  save(KEYS.content, content);
  return content;
}

// ---- Products ----
export function getProducts() {
  return getOrInit(KEYS.products, defaultProducts);
}

export function getLiveProducts() {
  return getProducts().filter(p => p.status === 'live').sort((a, b) => a.sort_order - b.sort_order);
}

export function getAllProducts() {
  return getProducts().sort((a, b) => a.sort_order - b.sort_order);
}

export function getProductBySlug(slug) {
  return getProducts().find(p => p.slug === slug) || null;
}

export function getProductById(id) {
  return getProducts().find(p => p.id === id) || null;
}

export function saveProduct(product) {
  const products = getProducts();
  const index = products.findIndex(p => p.id === product.id);
  if (index >= 0) {
    products[index] = product;
  } else {
    // New product — assign next id
    product.id = products.length > 0 ? Math.max(...products.map(p => p.id)) + 1 : 1;
    products.push(product);
  }
  save(KEYS.products, products);
  return product;
}

export function deleteProduct(id) {
  const products = getProducts().filter(p => p.id !== id);
  save(KEYS.products, products);
  return products;
}

// ---- Shipping Zones ----
export function getShippingZones() {
  return getOrInit(KEYS.shipping, defaultShipping);
}

export function saveShippingZones(zones) {
  save(KEYS.shipping, zones);
  return zones;
}

// ---- Email Templates ----
export function getEmailTemplates() {
  return getOrInit(KEYS.emails, defaultEmails);
}

export function saveEmailTemplate(template) {
  const templates = getEmailTemplates();
  const index = templates.findIndex(t => t.id === template.id);
  if (index >= 0) templates[index] = template;
  save(KEYS.emails, templates);
  return templates;
}

// ---- Promo Codes ----
export function getPromoCodes() {
  return getOrInit(KEYS.promos, defaultPromos);
}

export function savePromoCode(promo) {
  const promos = getPromoCodes();
  const index = promos.findIndex(p => p.id === promo.id);
  if (index >= 0) {
    promos[index] = promo;
  } else {
    promo.id = promos.length > 0 ? Math.max(...promos.map(p => p.id)) + 1 : 1;
    promos.push(promo);
  }
  save(KEYS.promos, promos);
  return promos;
}

export function validatePromoCode(code) {
  const promos = getPromoCodes();
  const promo = promos.find(p => p.code.toUpperCase() === code.toUpperCase() && p.is_active);
  return promo || null;
}

// ---- Orders (local only, for flow testing) ----
export function getOrders() {
  return getOrInit(KEYS.orders, []);
}

export function saveOrder(order) {
  const orders = getOrders();
  const index = orders.findIndex(o => o.id === order.id);
  if (index >= 0) {
    orders[index] = order;
  } else {
    order.id = orders.length > 0 ? Math.max(...orders.map(o => o.id)) + 1 : 1;
    order.order_number = 'AM-' + String(orders.length + 138).padStart(5, '0');
    order.created_at = new Date().toISOString();
    orders.unshift(order);
  }
  save(KEYS.orders, orders);
  return order;
}

// ---- Contact Messages ----
export function getContactMessages() {
  return getOrInit(KEYS.messages, []);
}

export function saveContactMessage(msg) {
  const messages = getContactMessages();
  msg.id = messages.length > 0 ? Math.max(...messages.map(m => m.id)) + 1 : 1;
  msg.created_at = new Date().toISOString();
  msg.is_read = false;
  messages.unshift(msg);
  save(KEYS.messages, messages);
  return msg;
}

// ---- Newsletter ----
export function getSubscribers() {
  return getOrInit(KEYS.subscribers, []);
}

export function addSubscriber(email) {
  const subs = getSubscribers();
  if (subs.find(s => s.email === email)) return subs;
  subs.push({ id: subs.length + 1, email, subscribed_at: new Date().toISOString(), is_active: true });
  save(KEYS.subscribers, subs);
  return subs;
}

// ---- Collection Pages ----
export function getCollectionPages() {
  return getOrInit(KEYS.pages, defaultPages);
}

export function getLiveCollectionPages() {
  return getCollectionPages().filter(function(p) { return p.status === "live"; }).sort(function(a, b) { return a.sort_order - b.sort_order; });
}

export function getCollectionPagesByNav(navLabel) {
  return getLiveCollectionPages().filter(function(p) { return p.parent_nav === navLabel; });
}

export function getCollectionPageBySlug(slug) {
  return getCollectionPages().find(function(p) { return p.slug === slug; }) || null;
}

export function saveCollectionPage(page) {
  var pages = getCollectionPages();
  var index = pages.findIndex(function(p) { return p.id === page.id; });
  if (index >= 0) {
    pages[index] = page;
  } else {
    page.id = pages.length > 0 ? Math.max.apply(null, pages.map(function(p) { return p.id; })) + 1 : 1;
    pages.push(page);
  }
  save(KEYS.pages, pages);
  return page;
}

export function deleteCollectionPage(id) {
  var pages = getCollectionPages().filter(function(p) { return p.id !== id; });
  save(KEYS.pages, pages);
  return pages;
}

// ---- Reset to defaults ----
export function resetAllData() {
  Object.values(KEYS).forEach(key => localStorage.removeItem(key));
}
